import { SubrouteComponent } from '../../types';
import { StyledExecutionsTableWrapper } from '../../styles';
import {
  PaginationWrapper,
  StyledExecutionData,
  StyledSwitch,
  StyledSwitchWrapper,
  StyledTableScrollContainer,
  TableWrapper,
} from './styles';
import SmartConnectorService from '__services/SmartConnectorService';
import TablePagination from '__components/Tables/Big/TablePagination';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { TRow } from '__components/Kizen/Table';
import BasicTable from '__components/Tables/Basic';
import { useAsyncFn } from 'react-use';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { useColumns } from './columns';
import { DEFAULT_PAGINATION_SETTINGS, EXECUTION_ACTIONS } from './constants';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { useCallback, useEffect, useState } from 'react';
import useModal from '__components/Modals/useModal';
import { SmartConnectorExecution } from '__pages/SmartConnectors/types';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useTranslation } from 'react-i18next';
import { StyledSearchInput, StyledTableToolbar } from '../../styles';
import { KizenTypography } from '__app/typography';
import Input from '__components/Kizen/Input';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from '__services/AxiosService';
import { parseExecutionProgress } from './helpers';
import { Button } from '@kizen/kds/Button';
import { downloadFile } from 'pages/SmartConnectors/helpers';

export const ExecutionsTable: SubrouteComponent = ({
  smartConnector,
  access,
  runKey,
  metaData,
}) => {
  const { t } = useTranslation();
  const [showToats] = useToast();
  const {
    page,
    pageSize,
    ordering,
    search,
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
  } = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);

  const [includeDryRun, setIncludeDryRun] = useState(false);

  const [
    { value: { results = EMPTY_ARRAY, count = 0 } = {}, loading },
    fetchExecutions,
  ] = useAsyncFn(async () => {
    const { data } = await SmartConnectorService.getSmartConnectorExecutions(
      smartConnector.id!,
      {
        params: {
          page,
          page_size: pageSize,
          ordering,
          search,
          include_dry_run: includeDryRun,
        },
      }
    );
    return data;
  }, [
    page,
    pageSize,
    ordering,
    search,
    includeDryRun,
    smartConnector.id,
    runKey,
  ]);

  useEffect(() => {
    fetchExecutions();
  }, [fetchExecutions]);

  const [detailsModalProps, , { show: showDetailsModalProp }] = useModal();
  const [statusModalProps, , { show: showStatusModalProp }] = useModal();

  const [smartConnectorExecution, setSmartConnectorExecution] =
    useState<SmartConnectorExecution | null>(null);

  const onSelectAction = useCallback(
    async (
      { value }: { value: keyof typeof EXECUTION_ACTIONS },
      data: SmartConnectorExecution
    ) => {
      if (value === EXECUTION_ACTIONS.view) {
        showDetailsModalProp();
        setSmartConnectorExecution(data);
      }
      if (
        value === EXECUTION_ACTIONS.download_input ||
        value === EXECUTION_ACTIONS.download_report
      ) {
        const href =
          data[
            value === EXECUTION_ACTIONS.download_input
              ? 'input_file'
              : 'final_report'
          ]?.url;
        if (href) {
          downloadFile(href);
        }
      }
      if (value === EXECUTION_ACTIONS.cancel) {
        try {
          await SmartConnectorService.cancelSmartConnectorExecution({
            execution_flow_id: data.id,
          });
          fetchExecutions();
          showToats({
            message: t('Execution has been canceled'),
            variant: toastVariant.SUCCESS,
          });
        } catch (error) {
          showToats({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to cancel execution'),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [showDetailsModalProp, showToats, fetchExecutions, t]
  );

  const onViewDetails = useCallback(
    (data: SmartConnectorExecution) => {
      showStatusModalProp();
      setSmartConnectorExecution(data);
    },
    [showStatusModalProp]
  );

  const columns = useColumns({
    onSelectAction,
    onViewDetails,
    access,
    metaData,
  });

  const handleToggleDryRun = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeDryRun(e.target.checked);
  };

  return (
    <>
      <StyledExecutionsTableWrapper>
        <StyledTableToolbar>
          <StyledSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Executions')}
            value={actualSearch}
            onChange={handleChangeSearch}
          />
          <KizenTypography as="h2" type="header">
            {t('{{count}} Executions', {
              count,
            })}
          </KizenTypography>
          <StyledSwitchWrapper>
            <StyledSwitch
              onChange={handleToggleDryRun}
              checked={includeDryRun}
              removeMargin
              label={t('Dry Runs')}
            />
            <Button
              leftIcon="action-refresh"
              variant="text"
              onClick={fetchExecutions}
              color={loading ? 'inherit' : 'secondary'}
              disabled={loading}
            />
          </StyledSwitchWrapper>
        </StyledTableToolbar>
        <TableWrapper>
          <StyledTableScrollContainer bottom top gradient={<Gradient />}>
            <BasicTable
              stickyHeader
              head={<TRow head columns={columns} data={headData} />}
              data-qa="smartConnector-executions-table"
              staleData={loading}
            >
              {count ? null : <TRow />}
              {results.map((data: any) => (
                <TRow key={data.id} columns={columns} data={data} />
              ))}
            </BasicTable>
          </StyledTableScrollContainer>
        </TableWrapper>
        <PaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={count}
            onChangePage={handleChangePage}
            onChangePerPage={handleChangePageSize}
          />
        </PaginationWrapper>
      </StyledExecutionsTableWrapper>
      {detailsModalProps.show ? (
        <BasicModal
          {...detailsModalProps}
          heading={t('Execution Details')}
          leftBtn={null}
          buttonText={t('Close')}
          actionBtnColor="blue"
          size="medium"
        >
          <StyledExecutionData>
            {JSON.stringify(smartConnectorExecution || {}, null, 2)}
          </StyledExecutionData>
        </BasicModal>
      ) : null}
      {statusModalProps.show ? (
        <BasicModal
          {...statusModalProps}
          heading={t('Execution Progress')}
          leftBtn={null}
          buttonText={t('Close')}
          actionBtnColor="blue"
          size="medium"
        >
          <StyledExecutionData>
            {parseExecutionProgress(
              smartConnectorExecution?.step_progress,
              metaData
            )}
          </StyledExecutionData>
        </BasicModal>
      ) : null}
    </>
  );
};
